import React, { useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import {
  ConfigProvider,
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
  TimePickerProps,
} from 'antd';
import type {
  DatePickerProps as AntDatePickerProps,
  RangePickerProps as AntRangePickerProps,
} from 'antd/lib/date-picker';
import { nextVariant } from '../styles';
import { buildNextDatePickerTheme } from '../next/tokens';

// Re-export with antd's native `variant` omitted — consumers should use the wrapper's variant
export type DatePickerProps = Omit<AntDatePickerProps, 'variant'>;
export type RangePickerProps = Omit<AntRangePickerProps, 'variant'>;

type DatePickerVariant = 'next';

type DatePickerFieldProps = Omit<DatePickerProps, 'variant'> & {
  variant?: DatePickerVariant;
};

type RangePickerFieldProps = Omit<RangePickerProps, 'variant'> & {
  variant?: DatePickerVariant;
};

/**
 * Minimal CSS for DatePicker/RangePicker next variant. ConfigProvider handles:
 * height, border-radius, border-color, error border, text color, placeholder
 * color, disabled text + bg. DatePicker has no input-trigger component tokens
 * (hoverBorderColor, activeShadow, etc.) so hover/focus/error shadows need
 * CSS.
 */
const nextPickerMinimalStyles = css`
  /* Hover: border + shadow */
  &.ant-picker:hover:not(.ant-picker-focused):not(.ant-picker-disabled):not(
      .ant-picker-status-error
    ) {
    ${nextVariant.hover}
  }

  /* Focus + hover: pin focus styles */
  &.ant-picker-focused,
  &.ant-picker-focused:hover {
    ${nextVariant.focus}
  }

  /* Error (resting): border only, no shadow */
  &.ant-picker-status-error {
    box-shadow: none !important;
  }

  /* Error + hover: add shadow, pin border (needs !important to beat Ant's error ring) */
  &.ant-picker-status-error:hover:not(.ant-picker-focused):not(.ant-picker-disabled) {
    border-color: ${({ theme }) => theme.token.next.colorError};
    box-shadow: ${({ theme }) => theme.token.next.boxShadowError} !important;
  }

  /* Error + focus (with or without hover) */
  &.ant-picker-status-error.ant-picker-focused,
  &.ant-picker-status-error.ant-picker-focused:hover {
    border-color: ${({ theme }) => theme.token.next.colorError};
    box-shadow: ${({ theme }) => theme.token.next.boxShadowError} !important;
  }

  /* Disabled: border matches bg */
  &.ant-picker-disabled {
    ${nextVariant.disabled}
  }

  /* Disabled with value: font-weight 500; placeholder stays at default 400 */
  &.ant-picker-disabled .ant-picker-input > input:not(:placeholder-shown) {
    font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
  }
`;

const StyledDatePicker = styled(AntDatePicker)<{ $variant?: DatePickerVariant }>`
  ${({ $variant }) => $variant === 'next' && nextPickerMinimalStyles}
` as unknown as React.ComponentType<AntDatePickerProps & { $variant?: DatePickerVariant }>;

const StyledRangePicker = styled(AntDatePicker.RangePicker)<{ $variant?: DatePickerVariant }>`
  ${({ $variant }) =>
    $variant === 'next' &&
    css`
      ${nextPickerMinimalStyles}

      .ant-picker-separator svg {
        display: none;
      }

      .ant-picker-separator::after {
        content: '→';
        color: ${({ theme }) => theme.token.next.palette.grey400};
      }
    `}
` as unknown as React.ComponentType<AntRangePickerProps & { $variant?: DatePickerVariant }>;

export const DatePicker: React.FC<DatePickerFieldProps> = ({
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  ...rest
}) => {
  const { token } = useTheme();
  const nextDatePickerTheme = useMemo(() => buildNextDatePickerTheme(token.next), [token.next]);
  const element = <StyledDatePicker $variant={variant} size={size} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextDatePickerTheme}>{element}</ConfigProvider>;
  }

  return element;
};

export const RangePicker: React.FC<RangePickerFieldProps> = ({
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  ...rest
}) => {
  const { token } = useTheme();
  const nextDatePickerTheme = useMemo(() => buildNextDatePickerTheme(token.next), [token.next]);
  const element = <StyledRangePicker $variant={variant} size={size} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextDatePickerTheme}>{element}</ConfigProvider>;
  }

  return element;
};

const StyledTimePicker = styled(AntTimePicker)<{ $variant?: DatePickerVariant }>`
  ${({ $variant }) => $variant === 'next' && nextPickerMinimalStyles}
` as unknown as React.ComponentType<TimePickerProps & { $variant?: DatePickerVariant }>;

type TimePickerFieldProps = Omit<TimePickerProps, 'variant'> & {
  variant?: DatePickerVariant;
};

export const TimePicker: React.FC<TimePickerFieldProps> = ({
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  ...rest
}) => {
  const { token } = useTheme();
  const nextDatePickerTheme = useMemo(() => buildNextDatePickerTheme(token.next), [token.next]);
  const element = <StyledTimePicker $variant={variant} size={size} {...rest} />;

  if (variant === 'next') {
    return <ConfigProvider theme={nextDatePickerTheme}>{element}</ConfigProvider>;
  }

  return element;
};
